import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  FormGroup,
  Label,
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
} from "reactstrap";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import TableContainer from "../../../../components/Common/TableContainer";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { fetchClaimApprovals, saveClaimApproval, fetchEmployeeDropdownAutoList } from "store/actions";
import Loader from "common/pageLoader";
import Swal from "sweetalert2";
import CustomSelect from "components/Common/custom-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import '../../../TimeSheet/Transactions/Approval/TimesheetAccordion.css';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

const TransactionClaimApproval = () => {
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const [data, setData] = useState([]);
  const [open, setOpen] = useState("");

  const { dropdownAutoList, claimApprovals, loading } = useSelector((state) => ({
    dropdownAutoList: state.EmployeeDetailsReducer?.dropdownAutoList ?? [],
    claimApprovals: state.ReimbursementClaimReducer?.claimApprovals ?? {},
    loading: state.ReimbursementClaimReducer?.loading || state.EmployeeDetailsReducer?.loading,
  }));

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0]);
    const unitId = authUserDetails[0].unit_id;
    dispatch(fetchEmployeeDropdownAutoList(unitId));
  }, [dispatch]);

  useEffect(() => {
    if (claimApprovals?.trip_list?.length > 0) {
      const claims = claimApprovals.trip_list.map(claim => ({
        ...claim,
        details: (claim.details || []).map(d => ({
          ...d,
          selectedOption: null,
          remarks_input: "",
          approved_amount: d.amount,
        })),
      }));
      setData(claims);
    } else {
      setData([]);
    }
  }, [claimApprovals]);

  const formik = useFormik({
    initialValues: { employee: "" },
    validationSchema: Yup.object({}),
    onSubmit: (values) => {
      dispatch(fetchClaimApprovals("1", values.employee?.value || ""));
    },
  });

  const toggle = useCallback((id) => {
    setOpen(prev => prev === id ? "" : id);
  }, []);

  const handleSave = () => {
    const allDetails = data.flatMap(claim => claim.details.filter(d => d.selectedOption));

    if (allDetails.length === 0) {
      Swal.fire({ icon: "warning", title: "No Selection", text: "Please select at least one approval action." });
      return;
    }

    const missingRemarks = allDetails.filter(
      d => d.selectedOption === "Reject" && (!d.remarks_input || d.remarks_input.trim() === "")
    );
    if (missingRemarks.length > 0) {
      Swal.fire({ icon: "warning", title: "Remarks Required", text: "Please enter remarks for rejected claims." });
      return;
    }

    dispatch(saveClaimApproval(allDetails));
  };

  const handleCancel = () => {
    formik.resetForm();
    setData([]);
    setOpen("");
  };

  const handleSelectAllForClaim = (claimId, action, checked) => {
    setData(prev => prev.map(claim =>
      claim.claim_id !== claimId ? claim : {
        ...claim,
        details: claim.details.map(d => ({ ...d, selectedOption: checked ? action : null }))
      }
    ));
  };

  const handleDetailOptionChange = (claimId, detailIdx, action) => {
    setData(prev => prev.map(claim =>
      claim.claim_id !== claimId ? claim : {
        ...claim,
        details: claim.details.map((d, i) =>
          i !== detailIdx ? d : { ...d, selectedOption: d.selectedOption === action ? null : action }
        )
      }
    ));
  };

  const handleDetailAmountChange = (claimId, detailIdx, value) => {
    setData(prev => prev.map(claim =>
      claim.claim_id !== claimId ? claim : {
        ...claim,
        details: claim.details.map((d, i) =>
          i !== detailIdx ? d : { ...d, approved_amount: value }
        )
      }
    ));
  };

  const handleDetailRemarksChange = (claimId, detailIdx, value) => {
    setData(prev => prev.map(claim =>
      claim.claim_id !== claimId ? claim : {
        ...claim,
        details: claim.details.map((d, i) =>
          i !== detailIdx ? d : { ...d, remarks_input: value }
        )
      }
    ));
  };

  const renderFilePreview = (filePath) => {
    if (!filePath) return <span className="text-muted">—</span>;
    const url = `${BASE_URL}${filePath}`;
    const isPdf = filePath.toLowerCase().endsWith(".pdf");
    if (isPdf) {
      return (
        <a href={url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-secondary">
          <i className="fas fa-file-pdf me-1" /> View PDF
        </a>
      );
    }
    return (
      <a href={url} target="_blank" rel="noreferrer">
        <img src={url} alt="attachment" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 4, border: "1px solid #ddd" }} />
      </a>
    );
  };

  const buildDetailColumns = (claimId) => [
    {
      Header: "S.No",
      Cell: ({ row }) => row.index + 1,
      disableFilters: true,
      disableSortBy: true,
    },
    { Header: "Head", accessor: "head_name" },
    { Header: "Sub-Head", accessor: "subhead_name" },
    { Header: "Amount", accessor: "amount" },
    {
      Header: "Approved Amount",
      accessor: "approved_amount",
      Cell: ({ row }) => (
        <Input
          type="number"
          value={row.original.approved_amount ?? row.original.amount}
          onChange={(e) => handleDetailAmountChange(claimId, row.index, e.target.value)}
          style={{ width: "100px" }}
        />
      ),
    },
    { Header: "Remarks", accessor: "remarks" },
    {
      Header: "Approval Remarks",
      Cell: ({ row }) => (
        <Input
          type="textarea"
          rows={1}
          value={row.original.remarks_input || ""}
          onChange={(e) => handleDetailRemarksChange(claimId, row.index, e.target.value)}
          style={{ minWidth: "130px" }}
        />
      ),
    },
    {
      Header: "Attachment",
      accessor: "file_path",
      disableFilters: true,
      disableSortBy: true,
      Cell: ({ value }) => renderFilePreview(value),
    },
    {
      Header: () => <div className="text-center">Approve</div>,
      accessor: "approve_action",
      disableFilters: true,
      disableSortBy: true,
      Cell: ({ row }) => (
        <div className="d-flex justify-content-center">
          <Input
            type="checkbox"
            checked={row.original.selectedOption === "Approve"}
            onChange={() => handleDetailOptionChange(claimId, row.index, "Approve")}
          />
        </div>
      ),
    },
    {
      Header: () => <div className="text-center">Reject</div>,
      accessor: "reject_action",
      disableFilters: true,
      disableSortBy: true,
      Cell: ({ row }) => (
        <div className="d-flex justify-content-center">
          <Input
            type="checkbox"
            checked={row.original.selectedOption === "Reject"}
            onChange={() => handleDetailOptionChange(claimId, row.index, "Reject")}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="page-content">
      <Loader loading={loading} />
      <Container fluid>
        <Breadcrumbs title="Reimbursement" breadcrumbItem="Transaction Claim Approval" />

        <Card>
          <CardBody>
            <Row className="mb-3">
              <Col md={4}>
                <FormGroup>
                  <Label>Employee</Label>
                  <CustomSelect
                    name="employee"
                    options={(dropdownAutoList || []).map(emp => ({ value: emp.emp_id.toString(), label: emp.emp_no }))}
                    value={formik.values.employee || null}
                    onChange={(selectedOption) => formik.setFieldValue("employee", selectedOption)}
                  />
                </FormGroup>
              </Col>
              <Col className="d-flex align-items-center justify-content-end">
                <Button type="button" className="btn button-search me-2" onClick={formik.handleSubmit}>
                  <i className="fas fa-search me-1" /> Search
                </Button>
                <Button type="button" className="btn button-cancel" onClick={handleCancel}>
                  <i className="fas fa-sync me-1" /> Cancel
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {data.length > 0 && (
          <Card>
            <CardBody>
              <div className="d-flex justify-content-end mb-3">
                <Button className="btn button-save" onClick={handleSave}>
                  <i className="fas fa-save me-1" /> Update
                </Button>
              </div>

              <Accordion open={open} toggle={toggle} className="w-100">
                {data.map((claim, idx) => (
                  <AccordionItem key={claim.claim_id} className="border-0 box-shadow mb-2">
                    <AccordionHeader targetId={idx.toString()} className={`accordion-header ${open === idx.toString() ? "accordion-open" : ""}`}>
                      <div
                        className="timesheet-summary timeaccsheet"
                        style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", alignItems: "center", width: "100%" }}
                      >
                        <div>
                          <p className="employee-name mb-0"><strong>{claim.trip_name}</strong></p>
                          {(claim.from_place || claim.to_place) && (
                            <small className="employee-department">
                              {claim.from_place} → {claim.to_place}
                            </small>
                          )}
                        </div>
                        <div className="text-center">
                          <small className="employee-department">Date: <strong>{claim.date}</strong></small>
                        </div>
                        <div style={{ textAlign: "right", paddingRight: "10px" }}>
                          <small className="employee-department">
                            {claim.from_date} — {claim.to_date}
                          </small>
                        </div>
                      </div>
                    </AccordionHeader>
                    <AccordionBody accordionId={idx.toString()}>
                      {open === idx.toString() && (
                        <>
                          {claim.comments && (
                            <p className="text-muted mb-2 px-1"><strong>Comments:</strong> {claim.comments}</p>
                          )}
                          <div className="d-flex justify-content-end mb-2 px-1">
                            <span className="me-3">
                              Approve All:
                              <Input
                                type="checkbox"
                                className="d-inline-block ms-1"
                                onChange={(e) => handleSelectAllForClaim(claim.claim_id, "Approve", e.target.checked)}
                              />
                            </span>
                            <span>
                              Reject All:
                              <Input
                                type="checkbox"
                                className="d-inline-block ms-1"
                                onChange={(e) => handleSelectAllForClaim(claim.claim_id, "Reject", e.target.checked)}
                              />
                            </span>
                          </div>
                          <div className="timesheet-table">
                            <TableContainer
                              columns={buildDetailColumns(claim.claim_id)}
                              data={claim.details || []}
                              customPageSize={10}
                              isGlobalFilter={false}
                            />
                          </div>
                        </>
                      )}
                    </AccordionBody>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardBody>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default TransactionClaimApproval;
